import type { ComponentProps } from 'react';
import { TdsIcon } from '@scania/tegel-react';

export const appRoutePaths = {
  access: '/self-service',
  apis: '/api-docs',
  catalog: '/catalog',
  catalogGraph: '/catalog-graph',
  docs: '/docs',
  groups: '/groups',
  home: '/',
  import: '/catalog-import',
  search: '/search',
  settings: '/settings',
} as const;

export const appRoutePatterns = {
  catalogEntity: `${appRoutePaths.catalog}/:namespace/:kind/:name`,
  techDocsReader: `${appRoutePaths.docs}/:namespace/:kind/:name`,
  techDocsEntity: `${appRoutePaths.docs}/:namespace/:kind/:name/*`,
} as const;

export const catalogEntityRoutePath = ({
  kind,
  namespace,
  name,
}: {
  kind: string;
  namespace: string;
  name: string;
}) =>
  `${appRoutePaths.catalog}/${encodeURIComponent(
    namespace,
  )}/${encodeURIComponent(kind.toLowerCase())}/${encodeURIComponent(name)}`;

export const catalogKindRoutePath = (kind: string) =>
  `${appRoutePaths.catalog}?filters[kind]=${encodeURIComponent(
    kind.toLowerCase(),
  )}`;

export const catalogGettingStartedRoutePath = `${appRoutePaths.catalog}?view=getting-started`;

export const catalogIntroRoutePath = catalogGettingStartedRoutePath;

export const catalogEntitiesRoutePath = `${appRoutePaths.catalog}?view=entities&filters[kind]=component&limit=20&offset=0`;

export const apiGettingStartedRoutePath = `${appRoutePaths.apis}?view=getting-started`;

export const apiEntitiesRoutePath = `${appRoutePaths.apis}?view=apis`;

export const docsGettingStartedRoutePath = `${appRoutePaths.docs}?view=getting-started`;

export const docsEntitiesRoutePath = `${appRoutePaths.docs}?view=documentation`;

export const importGettingStartedRoutePath = `${appRoutePaths.import}?view=getting-started`;

export const importComponentRoutePath = `${appRoutePaths.import}?view=import`;

export const groupsGettingStartedRoutePath = `${appRoutePaths.groups}?view=getting-started`;

export const groupsEntitiesRoutePath = `${appRoutePaths.groups}?view=groups`;

export const accessGettingStartedRoutePath = `${appRoutePaths.access}?view=getting-started`;

export const accessServicesRoutePath = `${appRoutePaths.access}?view=services`;

export const techDocsRoutePath = ({
  kind,
  namespace,
  name,
  path,
}: {
  kind: string;
  namespace: string;
  name: string;
  path?: string;
}) => {
  const basePath = `${appRoutePaths.docs}/${encodeURIComponent(
    namespace,
  )}/${encodeURIComponent(kind.toLowerCase())}/${encodeURIComponent(name)}`;

  if (!path) {
    return basePath;
  }

  return `${basePath}/${path.replace(/^\/+/, '')}`;
};

export const searchRoutePath = (query?: string) =>
  query
    ? `${appRoutePaths.search}?query=${encodeURIComponent(query)}`
    : appRoutePaths.search;

export type TdsIconName = ComponentProps<typeof TdsIcon>['name'];

export type SidebarItemConfig = {
  children?: Array<{
    label: string;
    to: string;
  }>;
  defaultTo?: string;
  end?: boolean;
  external?: boolean;
  icon: TdsIconName;
  label: string;
  to: string;
};

export const sidebarItems: SidebarItemConfig[] = [
  { end: true, icon: 'home', label: 'Home', to: appRoutePaths.home },
  {
    icon: 'folder',
    label: 'Catalog',
    to: catalogEntitiesRoutePath,
  },
  {
    icon: 'dashboard',
    label: 'APIs',
    to: apiEntitiesRoutePath,
  },
  {
    icon: 'document',
    label: 'Docs',
    to: docsEntitiesRoutePath,
  },
  {
    icon: 'upload',
    label: 'Import',
    to: importComponentRoutePath,
  },
  {
    icon: 'profile',
    label: 'My Groups',
    to: groupsEntitiesRoutePath,
  },
  {
    icon: 'lock',
    label: 'Access',
    to: accessServicesRoutePath,
  },
];

export const utilitySidebarItems: SidebarItemConfig[] = [
  {
    external: true,
    icon: 'cart',
    label: 'SCANIA-NOW',
    to: 'https://scanianow.scania.com/esc?id=ec_pro_dashboard_new',
  },
];
